import { useCallback, useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ListView } from "@/services/list-view";

export const RECENTLY_VIEWED_VIEW_ID = "recently_viewed";

export function buildRecentlyViewedView(defaultColumns: string[]): ListView {
    return {
        id: RECENTLY_VIEWED_VIEW_ID,
        name: "Récemment consultés",
        columns: defaultColumns,
        filters: {},
        order_by: null,
        created_by: { id: 0, username: "", first_name: "", last_name: "" },
        reference_type: 0,
        created_at: "",
        updated_at: "",
        is_pinned: false
    } as unknown as ListView;
}

export function useObjectListViews(views: ListView[], viewsLoading: boolean, defaultVisibleFields?: string[]) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentView, setCurrentView] = useState<ListView | null>(null);
    const [columnsManuallyModified, setColumnsManuallyModified] = useState(false);
    const [manualViewChangeInProgress, setManualViewChangeInProgress] = useState(false);

    // Default view configuration (memoized to avoid recreating on every render)
    const defaultView = useMemo(
        () =>
            ({
                id: "default", // Ajout d'un id stable pour la Default View
                name: "Default View",
                columns: defaultVisibleFields || ["id", "name", "created_at"],
                filters: {},
                order_by: null,
                created_by: { id: 0, username: "", first_name: "", last_name: "" },
                reference_type: 0,
                created_at: "",
                updated_at: "",
                is_pinned: false
            }) as unknown as ListView,
        [defaultVisibleFields]
    );

    const recentlyViewedView = useMemo(
        () => buildRecentlyViewedView(defaultVisibleFields || ["id", "name", "created_at"]),
        [defaultVisibleFields]
    );

    // Get current view ID from URL
    const listViewIdParam = searchParams.get("list_view_id");
    const listViewIdFromUrl = listViewIdParam ? parseInt(listViewIdParam, 10) : undefined;
    const viewParam = searchParams.get("view");
    const isRecentlyViewed = viewParam === RECENTLY_VIEWED_VIEW_ID;

    // Initialize and handle view changes
    useEffect(() => {
        console.log("🟪 useObjectListViews useEffect triggered:", {
            viewsLoading,
            manualViewChangeInProgress,
            listViewIdFromUrl,
            isRecentlyViewed,
            viewsLength: views.length,
            currentViewName: currentView?.name
        });

        if (viewsLoading || manualViewChangeInProgress) {
            console.log("🟪 Skipping automatic view change - loading or manual change in progress");
            return;
        }

        // Synthetic recently-viewed view: set currentView and skip list_view_id auto-init
        if (isRecentlyViewed) {
            setCurrentView(prevCurrentView => {
                if (prevCurrentView?.id === RECENTLY_VIEWED_VIEW_ID) {
                    return prevCurrentView;
                }
                console.log("🟪 Auto-setting recently viewed synthetic view");
                return recentlyViewedView;
            });
            return;
        }

        // Use setCurrentView callback to read current value without dependency
        setCurrentView(prevCurrentView => {
            // Si la vue sélectionnée existe toujours mais a changé (ex: renommée, save, etc.)
            if (prevCurrentView && prevCurrentView.id && views.length > 0) {
                const updated = views.find(v => v.id === prevCurrentView.id);
                if (updated) {
                    // Compare toutes les propriétés importantes
                    const hasChanged =
                        updated.name !== prevCurrentView.name ||
                        JSON.stringify(updated.filters) !== JSON.stringify(prevCurrentView.filters) ||
                        JSON.stringify(updated.filter_tree ?? null) !== JSON.stringify(prevCurrentView.filter_tree ?? null) ||
                        JSON.stringify(updated.columns) !== JSON.stringify(prevCurrentView.columns) ||
                        JSON.stringify(updated.order_by) !== JSON.stringify(prevCurrentView.order_by) ||
                        ((updated as any).filter_logic || "") !== ((prevCurrentView as any).filter_logic || "");
                    if (hasChanged) {
                        console.log("🟪 Mise à jour automatique de currentView après modification de la vue (ex: save, renommage, etc.)");
                        return updated;
                    }
                }
            }

            // If URL has a specific view ID, try to load it
            if (listViewIdFromUrl) {
                const view = views.find(v => v.id === listViewIdFromUrl);
                if (view && (!prevCurrentView || prevCurrentView.id !== view.id)) {
                    console.log("🟪 Auto-setting view from URL:", view.name);
                    return view;
                }

                // If view ID from URL doesn't exist in views, remove the parameter
                if (!view) {
                    console.log("🟪 View ID from URL not found, removing parameter");
                    const params = new URLSearchParams(searchParams);
                    params.delete("list_view_id");
                    setSearchParams(params, { replace: true });
                }
            }

            // If no URL view ID and we have views, use pinned or first view
            if (!listViewIdFromUrl && views.length > 0 && !prevCurrentView?.id) {
                const selectedView = views.find(v => v.is_pinned) || views[0];
                if (selectedView) {
                    console.log("🟪 Auto-setting default view:", selectedView.name);

                    // Update URL
                    const params = new URLSearchParams(searchParams);
                    params.set("list_view_id", selectedView.id.toString());
                    setSearchParams(params, { replace: true });

                    return selectedView;
                }
            }

            // If no saved views, use default view
            if (views.length === 0 && !prevCurrentView) {
                console.log("🟪 Auto-setting fallback default view");
                return defaultView as ListView;
            }

            // No change needed
            return prevCurrentView;
        });
    }, [listViewIdFromUrl, isRecentlyViewed, recentlyViewedView, views, viewsLoading, manualViewChangeInProgress]);

    // Handle view change
    const handleViewChange = useCallback((view: ListView) => {
        console.log("🟣 handleViewChange called with:", view.name, "- blocking automatic changes");
        setManualViewChangeInProgress(true);
        setCurrentView(view);
        setColumnsManuallyModified(false);

        // Update URL parameter
        const params = new URLSearchParams(searchParams);
        if (view.id === RECENTLY_VIEWED_VIEW_ID) {
            params.set("view", RECENTLY_VIEWED_VIEW_ID);
            params.delete("list_view_id");
        } else {
            params.delete("view");
            if (view.id) {
                params.set("list_view_id", view.id.toString());
            } else {
                params.delete("list_view_id");
            }
        }
        setSearchParams(params, { replace: true });

        // Re-enable automatic changes after a short delay
        setTimeout(() => {
            console.log("🟣 Re-enabling automatic view changes");
            setManualViewChangeInProgress(false);
        }, 1000);
    }, []);

    return {
        currentView,
        setCurrentView,
        handleViewChange,
        columnsManuallyModified,
        setColumnsManuallyModified,
        defaultView
    };
}
